import { useTranslations } from '@lang';
import { useCallback } from 'react';

const GUEST_FIELDS = ['title', 'firstName', 'lastName', 'gender'] as const;

type GuestField = (typeof GUEST_FIELDS)[number];

export type GuestFieldTooShort = { type: 'too_small'; minimum: number };

export type GuestFieldFlags = Partial<Record<GuestField, unknown>>;

const isTooShort = (flag: unknown): flag is GuestFieldTooShort =>
  typeof flag === 'object' &&
  flag !== null &&
  (flag as GuestFieldTooShort).type === 'too_small' &&
  typeof (flag as GuestFieldTooShort).minimum === 'number';

export const useGuestFieldErrors = () => {
  const { t } = useTranslations();

  return useCallback(
    (flags?: GuestFieldFlags): Partial<Record<GuestField, string>> | undefined => {
      if (!flags) return undefined;
      const labels: Record<GuestField, string> = {
        title: t('common.title'),
        firstName: t('common.firstName'),
        lastName: t('common.lastName'),
        gender: t('common.gender'),
      };
      const invalid = GUEST_FIELDS.filter((field) => flags[field]);
      if (invalid.length === 0) return undefined;
      return Object.fromEntries(
        invalid.map((field) => {
          const flag = flags[field];
          const message = isTooShort(flag)
            ? t('error.fieldTooShort', { label: labels[field], minimum: flag.minimum })
            : t('error.fieldRequired', { label: labels[field] });
          return [field, message];
        })
      );
    },
    [t]
  );
};
